import styled from '@emotion/styled';

import { Button, Link } from 'components/ui';
import { useAuth } from '../../hooks';
import { useHeaderAffiliate } from '../../hooks/useHeaderAffiliate';
import { CircleQuestionMark } from 'lucide-react';
import { useState } from 'react';
import WhatIsAybleModal, {
  getModalVariant,
} from 'components/HeaderNav/WhatIsAybleModal';
import { useTracking } from '../../hooks/useTracking';
import { TRACKING_SIGNUP_HELP_MODAL_VIEW } from '../OnboardingForm/constants';

const aybleLogo = '/FA__ayble_health_Logo_Isabelline.png';

const Topbar = styled('header')`
  background: #ffffff;
  border-bottom: 1px solid #e4edf0;
  position: sticky;
  top: 0;
  z-index: 40;
`;

const Inner = styled('div')`
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1040px;
  padding: 14px 20px;

  @media (max-width: 700px) {
    gap: 10px;
    padding: 12px 14px;
  }
`;

const BrandLock = styled('div')`
  align-items: center;
  display: flex;
  gap: 12px;
  /* Reserves the height of the "Is Ayble right for me?" button so the header
     does not change height on the screens that do not show it. */
  min-height: 48px;
  min-width: 0;

  @media (max-width: 700px) {
    gap: 8px;
  }
`;

const AybleMark = styled('img')`
  display: block;
  height: 32px;
  width: auto;

  @media (max-width: 700px) {
    height: 26px;
  }
`;

const CoBrand = styled('div')`
  align-items: center;
  border-right: 1px solid #dbe5e8;
  display: flex;
  padding-right: 12px;

  @media (max-width: 700px) {
    padding-right: 8px;
  }
`;

// Plain <img> so the partner logo keeps its natural aspect ratio at a fixed
// height: Chakra Image can collapse to width 0 without an explicit width.
const PartnerMark = styled('img')`
  display: block;
  height: 26px;
  max-width: 132px;
  object-fit: contain;
  width: auto;
`;

const HeaderNav = ({
  screenName,
  showWhatIsAybleModal,
}: {
  screenName?: string;
  showWhatIsAybleModal?: boolean;
}) => {
  const { isAuthenticated, signOut } = useAuth();
  const { affiliate, cobrand } = useHeaderAffiliate();
  const [modalOpen, setModalOpen] = useState(false);
  const { track } = useTracking();

  return (
    <Topbar>
      <Inner>
        <BrandLock>
          {cobrand && !!affiliate?.logo && (
            <CoBrand>
              <PartnerMark
                src={affiliate.logo}
                alt={`${affiliate.name} logo`}
                data-testid="header-cobranding-logo"
              />
            </CoBrand>
          )}

          <AybleMark src={aybleLogo} alt="Ayble Health logo" />
        </BrandLock>

        {showWhatIsAybleModal && (
          <Button
            variant={'outline'}
            paddingX={4.5}
            onClick={() => {
              track(TRACKING_SIGNUP_HELP_MODAL_VIEW, {
                screen_name: screenName,
                modal_variant: getModalVariant(affiliate),
              });
              setModalOpen(true);
            }}
          >
            <CircleQuestionMark />
            Is Ayble right for me?
          </Button>
        )}

        {isAuthenticated && (
          <Link
            color="secondary.500"
            fontSize="xs"
            isExternal={false}
            isUnderlined={false}
            textTransform="none"
            _hover={{ color: 'secondary.200' }}
            onClick={() => signOut()}
            style={{ cursor: 'pointer' }}
          >
            Logout
          </Link>
        )}
      </Inner>

      {showWhatIsAybleModal && (
        <WhatIsAybleModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          affiliate={affiliate}
        />
      )}
    </Topbar>
  );
};

export default HeaderNav;
